import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * TabBar — shared route-based tab strip used by Staff, Operations, and Assets hubs.
 * Shows edge fade hints and scroll arrows when tabs overflow on narrow viewports.
 */
export default function TabBar({ tabs, active, ariaLabel }) {
  const navRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollHints = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollHints();
    const el = navRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateScrollHints, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateScrollHints) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollHints);
      ro?.disconnect();
    };
  }, [tabs, updateScrollHints]);

  const scrollBy = (delta) => {
    navRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <div className="relative border-b border-ga-border-subtle">
      {canScrollLeft && (
        <>
          <div
            className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-ga-bg-page to-transparent"
            aria-hidden
          />
          <button
            type="button"
            onClick={() => scrollBy(-120)}
            className="absolute left-0 top-1/2 z-20 -translate-y-1/2 rounded-full border border-ga-border-subtle bg-ga-bg-surface p-1 text-ga-text-muted shadow-sm hover:text-ga-text-primary"
            aria-label="Scroll tabs left"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </>
      )}
      {canScrollRight && (
        <>
          <div
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-ga-bg-page to-transparent"
            aria-hidden
          />
          <button
            type="button"
            onClick={() => scrollBy(120)}
            className="absolute right-0 top-1/2 z-20 -translate-y-1/2 rounded-full border border-ga-border-subtle bg-ga-bg-surface p-1 text-ga-text-muted shadow-sm hover:text-ga-text-primary"
            aria-label="Scroll tabs right"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}
      <nav
        ref={navRef}
        className="-mb-px flex gap-1 overflow-x-auto px-1 [scrollbar-width:thin]"
        aria-label={ariaLabel}
      >
        {tabs.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.id;
          return (
            <Link
              key={t.id}
              to={t.to}
              aria-current={isActive ? 'page' : undefined}
              className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                isActive
                  ? 'border-ga-primary text-ga-primary'
                  : 'border-transparent text-ga-text-muted hover:text-ga-text-primary'
              }`}
            >
              {Icon && <Icon className="h-4 w-4" aria-hidden />}
              {t.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
